import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import { Stethoscope, ShoppingBag, Building2, ArrowRight, CheckCircle2, Heart, Scissors, Footprints, Truck } from "lucide-react";

const ROLES = [
  { id: "vet", label: "Vet Doctor", icon: Stethoscope, color: "bg-sky text-white", desc: "Run a clinic — manage appointments, vaccinations & follow-ups." },
  { id: "groomer", label: "Groomer", icon: Scissors, color: "bg-brand text-white", desc: "Offer bathing, haircuts, nail trim. Get bookings in 2 clicks." },
  { id: "walker", label: "Dog Walker", icon: Footprints, color: "bg-leaf text-ink", desc: "Pick your area & rates. Parents book walks on the app." },
  { id: "store_owner", label: "Pet Store Owner", icon: ShoppingBag, color: "bg-brand text-white", desc: "List products, manage inventory, fulfill orders." },
  { id: "hostel_owner", label: "Hostel Owner", icon: Building2, color: "bg-leaf text-ink", desc: "Receive bookings, manage capacity, track revenue." },
  { id: "ngo", label: "NGO / Shelter", icon: Heart, color: "bg-sunshine text-ink", desc: "Run rescues, fosters, adoptions. Built-in ABC 2023 register." },
  { id: "mobile_vet_van", label: "Mobile Vet Van", icon: Truck, color: "bg-sky text-white", desc: "Operate vaccination drives & checkup vans. Anyone can register one." },
];

export default function Partner() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [form, setForm] = useState({ business_name: "", city: "Noida", specialty: "General & Vaccinations", phone: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Clear partner intent stash
    try { localStorage.removeItem("petbond_partner_intent"); } catch {}
    // If already a vendor, route to their dashboard (super_admin stays to browse)
    if (user && user.role && user.role !== "parent" && user.role !== "super_admin") {
      const dest = dashFor(user.role);
      if (dest) navigate(`/app/${dest}`, { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (role === "mobile_vet_van") {
        // Van is NOT a role — register a van on the user's existing account.
        await axios.post(`${API}/mobile-van/register`, {
          name: form.business_name,
          city: form.city,
          phone: form.phone || null,
          van_registration_number: null,
          vet_on_staff: null,
        });
        setDone(true);
        setTimeout(() => navigate("/app/van-schedule", { replace: true }), 900);
      } else {
        await axios.post(`${API}/partner/switch-role`, { role, ...form });
        await refresh();
        setDone(true);
        setTimeout(() => navigate(`/app/${dashFor(role)}`, { replace: true }), 900);
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="partner-onboarding-page">
      <h1 className="font-heading text-3xl md:text-4xl font-extrabold">Become a PetBond partner</h1>
      <p className="text-ink-soft mt-2">Pick the role that fits your business. You can switch back to a regular pet parent any time.</p>

      {!role && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {ROLES.map(r => (
            <button
              key={r.id}
              data-testid={`role-${r.id}`}
              onClick={() => setRole(r.id)}
              className="bg-surface border border-line rounded-3xl p-6 text-left shadow-soft hover:-translate-y-1 hover:shadow-hover transition-all"
            >
              <div className={`w-11 h-11 rounded-2xl grid place-items-center mb-3 ${r.color}`}><r.icon className="w-5 h-5"/></div>
              <div className="font-heading text-lg font-bold">{r.label}</div>
              <div className="text-sm text-ink-soft mt-1">{r.desc}</div>
              <div className="mt-3 text-brand font-bold text-sm inline-flex items-center gap-1">Continue <ArrowRight className="w-3.5 h-3.5"/></div>
            </button>
          ))}
        </div>
      )}

      {role && !done && (
        <form onSubmit={submit} className="mt-8 bg-surface border border-line rounded-3xl p-6 shadow-soft space-y-4" data-testid="partner-form">
          <div className="flex items-center gap-3 mb-2">
            {(() => { const R = ROLES.find(x => x.id === role); const Icon = R.icon; return (
              <><div className={`w-10 h-10 rounded-2xl grid place-items-center ${R.color}`}><Icon className="w-5 h-5"/></div><div className="font-heading text-xl font-bold">{R.label}</div></>
            ); })()}
            <button type="button" onClick={() => setRole(null)} className="ml-auto text-sm text-ink-soft hover:text-brand">Change</button>
          </div>

          <Field label="Business name">
            <input data-testid="partner-business-name" required value={form.business_name} onChange={e=>setForm({...form, business_name:e.target.value})} className="pinput" placeholder={role === "vet" ? "PawCare Clinic" : (role === "store_owner" ? "TailWag Store" : "Happy Tails Boarding")} />
          </Field>
          <Field label="City">
            <input data-testid="partner-city" required value={form.city} onChange={e=>setForm({...form, city:e.target.value})} className="pinput" />
          </Field>
          {role === "vet" && (
            <Field label="Specialty">
              <select data-testid="partner-specialty" value={form.specialty} onChange={e=>setForm({...form, specialty:e.target.value})} className="pinput">
                {["General & Vaccinations","Surgery & Orthopedics","Dermatology & Allergies","Cat Specialist","Exotics (Birds, Rabbits)","Dental"].map(s=><option key={s}>{s}</option>)}
              </select>
            </Field>
          )}
          <Field label="Phone (optional)">
            <input data-testid="partner-phone" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} className="pinput" />
          </Field>

          <button disabled={saving} data-testid="partner-submit" className="w-full bg-brand text-white rounded-full py-3 font-bold hover:bg-brand-hover disabled:opacity-60">
            {saving ? "Setting up…" : "Open my dashboard"}
          </button>
        </form>
      )}

      {done && (
        <div className="mt-10 bg-leaf/30 border border-leaf rounded-3xl p-8 text-center" data-testid="partner-done">
          <CheckCircle2 className="w-12 h-12 text-ink mx-auto mb-3"/>
          <div className="font-heading text-2xl font-extrabold">You're all set!</div>
          <div className="text-ink-soft mt-1">Taking you to your dashboard…</div>
        </div>
      )}

      <style>{`.pinput{width:100%;border:1px solid #EAE0D5;border-radius:9999px;padding:0.7rem 1.1rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;color:#2C2C2C;outline:none;}.pinput:focus{border-color:#FF6B35;}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

function dashFor(role) {
  if (role === "vet") return "vet-dashboard";
  if (role === "store_owner") return "store-dashboard";
  if (role === "hostel_owner") return "hostel-dashboard";
  if (role === "ngo") return "ngo/impact";
  if (role === "groomer" || role === "walker") return "services-dashboard";
  return "";
}
